import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Globe, 
  Settings, 
  Save, 
  RotateCcw, 
  CheckCircle, 
  AlertTriangle,
  Info,
  ExternalLink
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import baseUrlManager from '../utils/baseUrlManager';

const ConfigContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid #e5e7eb;
`;

const ConfigTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConfigForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  
  &:disabled {
    background-color: #f9fafb;
    color: #6b7280;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background-color: #2563eb;
          color: white;
          &:hover:not(:disabled) {
            background-color: #1d4ed8;
          }
        `;
      case 'danger':
        return `
          background-color: #dc2626;
          color: white;
          &:hover:not(:disabled) {
            background-color: #b91c1c;
          }
        `;
      default:
        return `
          background-color: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover:not(:disabled) {
            background-color: #e5e7eb;
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusDisplay = styled.div<{ $status: 'success' | 'warning' | 'error' | 'info' }>`
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  ${props => {
    switch (props.$status) {
      case 'success':
        return `
          background-color: #d1fae5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        `;
      case 'warning':
        return `
          background-color: #fef3c7;
          color: #92400e;
          border: 1px solid #fde68a;
        `;
      case 'error':
        return `
          background-color: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        `;
      default:
        return `
          background-color: #dbeafe;
          color: #1e40af;
          border: 1px solid #bfdbfe;
        `;
    }
  }}
`;

const InfoText = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
`;

const CurrentUrlDisplay = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  color: #374151;
  
  strong {
    color: #1f2937;
  }
`;

interface BaseUrlConfigProps {
  onUrlChange?: (newUrl: string) => void;
}

const BaseUrlConfig: React.FC<BaseUrlConfigProps> = ({ onUrlChange }) => {
  const [baseUrl, setBaseUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'success' | 'warning' | 'error' | 'info' | null>(null);
  const [statusMessage, setStatusMessage] = useState('');

  // Get current URL information
  const getCurrentUrlInfo = () => {
    const currentUrl = window.location.href;
    const currentPath = window.location.pathname;
    const currentOrigin = window.location.origin;
    
    // Extract base path (everything before the last segment)
    const pathSegments = currentPath.split('/').filter(segment => segment);
    let basePath = '/';
    
    if (pathSegments.length > 0) {
      const lastSegment = pathSegments[pathSegments.length - 1];
      if (!lastSegment.includes('.') && lastSegment !== 'index.html') {
        basePath = '/' + pathSegments.join('/') + '/';
      } else if (pathSegments.length > 1) {
        basePath = '/' + pathSegments.slice(0, -1).join('/') + '/';
      }
    }
    
    return {
      currentUrl,
      currentPath,
      currentOrigin,
      basePath,
      suggestedBaseUrl: currentOrigin + basePath
    };
  };

  // Initialize with current URL
  useEffect(() => {
    const currentBaseUrl = baseUrlManager.getBaseUrl();
    setBaseUrl(currentBaseUrl);
  }, []);

  // Validate URL
  const validateUrl = (url: string): { isValid: boolean; message: string } => {
    if (!url.trim()) {
      return { isValid: false, message: 'Base URL is required' };
    }

    try {
      const urlObj = new URL(url);
      
      // Check if it's HTTPS or localhost
      if (urlObj.protocol !== 'https:' && urlObj.hostname !== 'localhost') {
        return { isValid: false, message: 'URL must be HTTPS or localhost' };
      }

      // Check if it ends with /
      if (!url.endsWith('/')) {
        return { isValid: false, message: 'URL should end with /' };
      }

      return { isValid: true, message: 'URL is valid' };
    } catch (error) {
      return { isValid: false, message: 'Invalid URL format' };
    }
  };

  // Save base URL configuration
  const handleSave = async () => {
    const validation = validateUrl(baseUrl);
    
    if (!validation.isValid) {
      setStatus('error');
      setStatusMessage(validation.message);
      toast.error(validation.message);
      return;
    }

    setIsLoading(true);
    setStatus('info');
    setStatusMessage('Saving base URL configuration...');

    try {
      // Use baseUrlManager to set the URL
      const success = baseUrlManager.setBaseUrl(baseUrl);
      
      if (success) {
        setStatus('success');
        setStatusMessage('Base URL configuration saved successfully!');
        toast.success('Base URL configuration saved!');
        
        // Notify parent component
        onUrlChange?.(baseUrl);
      } else {
        setStatus('error');
        setStatusMessage('Failed to save base URL configuration');
        toast.error('Failed to save base URL configuration');
      }
      
    } catch (error) {
      console.error('Error saving base URL:', error);
      setStatus('error');
      setStatusMessage('Failed to save base URL configuration');
      toast.error('Failed to save base URL configuration');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset to current URL
  const handleReset = () => {
    baseUrlManager.reset();
    setBaseUrl(baseUrlManager.getBaseUrl());
    setStatus(null);
    setStatusMessage('');
  };

  // Auto-detect current URL
  const handleAutoDetect = () => {
    baseUrlManager.reset();
    setBaseUrl(baseUrlManager.getBaseUrl());
    setStatus('info');
    setStatusMessage('Auto-detected current URL');
  };

  // Open URL in new tab
  const handleOpenUrl = () => {
    if (baseUrl) {
      window.open(baseUrl, '_blank');
    }
  };

  const urlInfo = baseUrlManager.getUrlInfo();

  return (
    <ConfigContainer>
      <ConfigTitle>
        <Globe size={18} />
        Base App URL Configuration
      </ConfigTitle>

      <ConfigForm onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <InputGroup>
          <Label htmlFor="baseUrl">Base App URL</Label>
          <Input
            id="baseUrl"
            type="url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://example.com/app/"
            disabled={isLoading}
          />
          <InfoText>
            This is the base URL where your PWA will be installed. 
            It should end with a forward slash (/) and be accessible via HTTPS.
          </InfoText>
        </InputGroup>

        <CurrentUrlDisplay>
          <strong>Current URL Info:</strong><br />
          Full URL: {urlInfo.currentUrl}<br />
          Base Path: {urlInfo.basePath}<br />
          Base URL: {urlInfo.baseUrl}<br />
          Within Scope: {urlInfo.isWithinScope ? 'Yes' : 'No'}
        </CurrentUrlDisplay>

        <ButtonGroup>
          <Button 
            type="submit" 
            $variant="primary" 
            disabled={isLoading}
          >
            <Save size={14} />
            {isLoading ? 'Saving...' : 'Save Configuration'}
          </Button>
          
          <Button 
            type="button" 
            $variant="secondary" 
            onClick={handleAutoDetect}
            disabled={isLoading}
          >
            <Settings size={14} />
            Auto-Detect
          </Button>
          
          <Button 
            type="button" 
            $variant="secondary" 
            onClick={handleReset}
            disabled={isLoading}
          >
            <RotateCcw size={14} />
            Reset
          </Button>
          
          {baseUrl && (
            <Button 
              type="button" 
              $variant="secondary" 
              onClick={handleOpenUrl}
              disabled={isLoading}
            >
              <ExternalLink size={14} />
              Open URL
            </Button>
          )}
        </ButtonGroup>
      </ConfigForm>

      {status && (
        <StatusDisplay $status={status}>
          {status === 'success' && <CheckCircle size={16} />}
          {status === 'warning' && <AlertTriangle size={16} />}
          {status === 'error' && <AlertTriangle size={16} />}
          {status === 'info' && <Info size={16} />}
          {statusMessage}
        </StatusDisplay>
      )}

      <InfoText style={{ marginTop: '12px' }}>
        <strong>Note:</strong> After changing the base URL, the PWA manifest will be updated automatically. 
        You may need to refresh the page for changes to take effect.
      </InfoText>
    </ConfigContainer>
  );
};

export default BaseUrlConfig;
